import { checkedShader } from './device'
import { shadowShader, tableShader } from './shaders'

export interface Pipelines {
  main: GPURenderPipeline
  shadow: GPURenderPipeline
  frameLayout: GPUBindGroupLayout
  objectLayout: GPUBindGroupLayout
  shadowFrameLayout: GPUBindGroupLayout
  shadowObjectLayout: GPUBindGroupLayout
}

export async function createPipelines(device: GPUDevice, format: GPUTextureFormat): Promise<Pipelines> {
  const [mainModule, shadowModule] = await Promise.all([
    checkedShader(device, tableShader, 'Table surface shader'),
    checkedShader(device, shadowShader, 'Table shadow shader'),
  ])
  const uniform = { type: 'uniform' as const }
  const both = GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT
  const frameLayout = device.createBindGroupLayout({ label: 'Frame layout', entries: [
    { binding: 0, visibility: both, buffer: uniform },
    { binding: 1, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'depth' } },
    { binding: 2, visibility: GPUShaderStage.FRAGMENT, sampler: { type: 'comparison' } },
  ] })
  const objectLayout = device.createBindGroupLayout({ label: 'Object layout', entries: [
    { binding: 0, visibility: both, buffer: uniform },
    { binding: 1, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },
    { binding: 2, visibility: GPUShaderStage.FRAGMENT, sampler: { type: 'filtering' } },
    { binding: 3, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },
  ] })
  const shadowFrameLayout = device.createBindGroupLayout({ label: 'Shadow frame layout', entries: [
    { binding: 0, visibility: GPUShaderStage.VERTEX, buffer: uniform },
  ] })
  const shadowObjectLayout = device.createBindGroupLayout({ label: 'Shadow object layout', entries: [
    { binding: 0, visibility: GPUShaderStage.VERTEX, buffer: uniform },
  ] })
  const buffers: GPUVertexBufferLayout[] = [{ arrayStride: 32, attributes: [
    { shaderLocation: 0, offset: 0, format: 'float32x3' },
    { shaderLocation: 1, offset: 12, format: 'float32x3' },
    { shaderLocation: 2, offset: 24, format: 'float32x2' },
  ] }]
  const [main, shadow] = await Promise.all([
    device.createRenderPipelineAsync({
      label: 'Table surfaces · 4× MSAA',
      layout: device.createPipelineLayout({ bindGroupLayouts: [frameLayout, objectLayout] }),
      vertex: { module: mainModule, entryPoint: 'tableVertex', buffers },
      fragment: { module: mainModule, entryPoint: 'tableFragment', targets: [{ format }] },
      primitive: { topology: 'triangle-list', cullMode: 'back' },
      depthStencil: { format: 'depth24plus', depthWriteEnabled: true, depthCompare: 'less' },
      multisample: { count: 4 },
    }),
    device.createRenderPipelineAsync({
      label: 'Directional shadow depth',
      layout: device.createPipelineLayout({ bindGroupLayouts: [shadowFrameLayout, shadowObjectLayout] }),
      vertex: { module: shadowModule, entryPoint: 'shadowVertex', buffers },
      primitive: { topology: 'triangle-list', cullMode: 'back' },
      depthStencil: { format: 'depth32float', depthWriteEnabled: true, depthCompare: 'less', depthBias: 2, depthBiasSlopeScale: 2 },
    }),
  ])
  return { main, shadow, frameLayout, objectLayout, shadowFrameLayout, shadowObjectLayout }
}
